// NutritionCertificate.js
// Printable FDA Nutrition Label Compliance Certificate.
// Cites 21 CFR 101.9 (labeling), 21 CFR 101.12 (RACC/serving size),
// documents data sources, rounding audit, and calculation methodology.

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { getNutritionCertificate } from '../services/nutritionService';
import { NutritionLabel } from './NutritionFactsPanel';

// ─────────────────────────────────────────────────────────────────────────────
// Regulatory reference data
// ─────────────────────────────────────────────────────────────────────────────

const ROUNDING_RULES = [
  { nutrient: 'Calories',            unit: 'kcal', cfr: '21 CFR 101.9(c)(1)',       rule: '< 5 kcal → 0; < 50 kcal → nearest 5; ≥ 50 kcal → nearest 10' },
  { nutrient: 'Total Fat',           unit: 'g',    cfr: '21 CFR 101.9(c)(2)',       rule: '< 0.5 g → 0; < 5 g → nearest 0.5 g; ≥ 5 g → nearest 1 g' },
  { nutrient: 'Saturated Fat',       unit: 'g',    cfr: '21 CFR 101.9(c)(2)(i)',    rule: 'Same as Total Fat' },
  { nutrient: 'Trans Fat',           unit: 'g',    cfr: '21 CFR 101.9(c)(2)(ii)',   rule: '< 0.5 g → 0; otherwise nearest 0.5 g' },
  { nutrient: 'Polyunsaturated Fat', unit: 'g',    cfr: '21 CFR 101.9(c)(2)(iii)',  rule: 'Same as Total Fat (voluntary)' },
  { nutrient: 'Monounsaturated Fat', unit: 'g',    cfr: '21 CFR 101.9(c)(2)(iv)',   rule: 'Same as Total Fat (voluntary)' },
  { nutrient: 'Cholesterol',         unit: 'mg',   cfr: '21 CFR 101.9(c)(3)',       rule: '< 2 mg → 0; 2–5 mg → 5 mg; ≥ 5 mg → nearest 5 mg' },
  { nutrient: 'Sodium',              unit: 'mg',   cfr: '21 CFR 101.9(c)(4)',       rule: '< 5 mg → 0; < 140 mg → nearest 5 mg; ≥ 140 mg → nearest 10 mg' },
  { nutrient: 'Total Carbohydrate',  unit: 'g',    cfr: '21 CFR 101.9(c)(6)',       rule: '< 0.5 g → 0; otherwise nearest 1 g' },
  { nutrient: 'Dietary Fiber',       unit: 'g',    cfr: '21 CFR 101.9(c)(6)(i)',    rule: '< 0.5 g → 0; otherwise nearest 1 g' },
  { nutrient: 'Total Sugars',        unit: 'g',    cfr: '21 CFR 101.9(c)(6)(ii)',   rule: '< 0.5 g → 0; otherwise nearest 1 g' },
  { nutrient: 'Added Sugars',        unit: 'g',    cfr: '21 CFR 101.9(c)(6)(iii)',  rule: '< 0.5 g → 0; otherwise nearest 1 g' },
  { nutrient: 'Protein',             unit: 'g',    cfr: '21 CFR 101.9(c)(7)',       rule: '< 0.5 g → 0; otherwise nearest 1 g' },
  { nutrient: 'Vitamin D',           unit: 'mcg',  cfr: '21 CFR 101.9(c)(8)(A)',    rule: 'Nearest 0.1 mcg' },
  { nutrient: 'Calcium',             unit: 'mg',   cfr: '21 CFR 101.9(c)(8)(B)',    rule: 'Nearest 1 mg' },
  { nutrient: 'Iron',                unit: 'mg',   cfr: '21 CFR 101.9(c)(8)(C)',    rule: 'Nearest 0.1 mg' },
  { nutrient: 'Potassium',           unit: 'mg',   cfr: '21 CFR 101.9(c)(8)(D)',    rule: 'Nearest 1 mg' },
];

const DAILY_VALUES = {
  totalFat: 78, saturatedFat: 20, cholesterol: 300, sodium: 2300,
  totalCarbs: 275, dietaryFiber: 28, addedSugars: 50,
  vitaminD: 20, calcium: 1300, iron: 18, potassium: 4700,
};

// Vitamins/minerals get FDA's tiered %DV rounding (21 CFR 101.9(c)(8)(iii)) — must run on the
// raw (unrounded) ratio; rounding to a whole percent first and then tiering double-rounds
// borderline values into the wrong band (e.g. true 2.5% DV would wrongly print as 4% instead of
// the correct 2%). Every other nutrient uses the simple whole-percent %DV rule.
const MICRO_DV_KEYS = ['vitaminD', 'calcium', 'iron', 'potassium'];
const fdaRoundMicroDV = (raw) => {
  if (raw == null) return null;
  if (raw === 0) return 0;
  if (raw < 2) return '<2';
  if (raw <= 10) return Math.round(raw / 2) * 2;
  if (raw <= 50) return Math.round(raw / 5) * 5;
  return Math.round(raw / 10) * 10;
};

const NUTRIENT_KEYS = [
  { key: 'calories',           label: 'Calories',            unit: 'kcal' },
  { key: 'totalFat',           label: 'Total Fat',           unit: 'g' },
  { key: 'saturatedFat',       label: 'Saturated Fat',       unit: 'g' },
  { key: 'transFat',           label: 'Trans Fat',           unit: 'g' },
  { key: 'polyunsaturatedFat', label: 'Polyunsaturated Fat', unit: 'g' },
  { key: 'monounsaturatedFat', label: 'Monounsaturated Fat', unit: 'g' },
  { key: 'cholesterol',        label: 'Cholesterol',         unit: 'mg' },
  { key: 'sodium',             label: 'Sodium',              unit: 'mg' },
  { key: 'totalCarbs',         label: 'Total Carbohydrate',  unit: 'g' },
  { key: 'dietaryFiber',       label: 'Dietary Fiber',       unit: 'g' },
  { key: 'totalSugars',        label: 'Total Sugars',        unit: 'g' },
  { key: 'addedSugars',        label: 'Added Sugars',        unit: 'g' },
  { key: 'protein',            label: 'Protein',             unit: 'g' },
  { key: 'vitaminD',           label: 'Vitamin D',           unit: 'mcg' },
  { key: 'calcium',            label: 'Calcium',             unit: 'mg' },
  { key: 'iron',               label: 'Iron',                unit: 'mg' },
  { key: 'potassium',          label: 'Potassium',           unit: 'mg' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Certificate document (pure display — used both on-screen and in print portal)
// ─────────────────────────────────────────────────────────────────────────────
const CertificateDoc = ({ data }) => {
  const { product, company, profile, generatedAt, coveragePct, isDraft, version } = data;
  const ingredientSources = [...(data.ingredientSources || [])].sort((a, b) => b.expectedQtyKg - a.expectedQtyKg);

  const fmt = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  const concentrationFactor = profile?.weightLossPct > 0
    ? (1 / (1 - profile.weightLossPct / 100)).toFixed(4)
    : 1;

  const page = {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '11px',
    color: '#000',
    background: '#fff',
    padding: '32px',
    maxWidth: '820px',
    margin: '0 auto',
  };

  const sectionTitle = {
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #1b2638',
    paddingBottom: '3px',
    marginBottom: '10px',
    marginTop: '20px',
    color: '#1b2638',
  };

  const th = { background: '#1b2638', color: '#fff', padding: '5px 8px', fontSize: '10px', textAlign: 'left', fontWeight: 'bold' };
  const td = { padding: '4px 8px', fontSize: '10px', borderBottom: '1px solid #eee', verticalAlign: 'top' };
  const tdAlt = { ...td, background: '#f8f9fa' };

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        borderBottom: '3px solid #1b2638', paddingBottom: '12px', marginBottom: '4px' }}>
        <div>
          {company?.logoUrl && (
            <img src={company.logoUrl} alt="logo" style={{ height: '40px', marginBottom: '6px', display: 'block' }} />
          )}
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1b2638' }}>
            Nutrition Label Compliance Certificate
          </div>
          <div style={{ fontSize: '15px', fontStyle: 'italic', color: '#1b2638', marginTop: '2px' }}>
            {product.name}
          </div>
          <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>
            {company?.name}{company?.city ? ` · ${company.city}${company.state ? `, ${company.state}` : ''}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '11px', color: '#555' }}>
          {product.sku && <div><strong>SKU:</strong> {product.sku}</div>}
          <div><strong>Generated:</strong> {fmtDate(generatedAt)}</div>
          {version ? (
            <div style={{ marginTop: '6px' }}>
              <span style={{ background: '#198754', color: '#fff', padding: '2px 8px',
                borderRadius: '3px', fontSize: '10px', fontWeight: 'bold' }}>
                APPROVED — Version {version.versionNumber}
              </span>
            </div>
          ) : (
            <div style={{ marginTop: '6px' }}>
              <span style={{ background: '#dc3545', color: '#fff', padding: '2px 8px',
                borderRadius: '3px', fontSize: '10px', fontWeight: 'bold' }}>
                DRAFT — NOT APPROVED
              </span>
              <div style={{ fontSize: '9px', color: '#dc3545', marginTop: '3px' }}>
                Approve &amp; lock this label before distributing
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Regulatory Compliance Checklist ── */}
      <div style={sectionTitle}>Regulatory Compliance</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
        <thead>
          <tr>
            <th style={th}>Regulation</th>
            <th style={th}>Description</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['21 CFR 101.9',           'Nutrition labeling of food — mandatory nutrient declarations',                   true],
            ['21 CFR 101.9(c)',         'Per-nutrient declaration requirements and rounding rules applied',               true],
            ['21 CFR 101.9(c)(8)(iv)', 'Daily Values based on 2,000 calorie diet reference amounts',                    true],
            ['21 CFR 101.9(d)',         'Label format — Nutrition Facts heading, nutrient order, type hierarchy',         true],
            ['21 CFR 101.12',           'Reference Amounts Customarily Consumed (RACC) used to determine serving size',  true],
            ['21 CFR 101.9(b)(2)',       'Serving size declared in common household measure and metric (g)',               true],
            ['21 CFR 101.9(g)',          'Calculation basis: weighted average of all recipe ingredients per serving',      true],
          ].map(([reg, desc, ok], i) => (
            <tr key={i}>
              <td style={i % 2 === 0 ? td : tdAlt}><strong>{reg}</strong></td>
              <td style={i % 2 === 0 ? td : tdAlt}>{desc}</td>
              <td style={{ ...(i % 2 === 0 ? td : tdAlt), color: ok ? '#198754' : '#dc3545', fontWeight: 'bold' }}>
                {ok ? '✓ Compliant' : '✗ Not met'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isDraft && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px',
          padding: '8px 12px', marginBottom: '8px', fontSize: '10px', color: '#856404' }}>
          ⚠ This certificate is based on a <strong>draft</strong> nutrition profile that has not been approved.
          Use the "Approve &amp; Lock" button in QuickLOG to create an official versioned record before distributing this document.
        </div>
      )}

      {/* ── Nutrition Label + Serving Size + Calculation — kept together on one page ── */}
      <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', marginTop: '8px' }}>

          <div style={{ flexShrink: 0 }}>
            <div style={sectionTitle}>Nutrition Facts Label</div>
            {profile ? <NutritionLabel profile={profile} compact={false} /> :
              <div style={{ color: '#dc3545', fontSize: '12px' }}>No nutrition profile saved.</div>}
          </div>

          <div style={{ flex: 1 }}>
            <div style={sectionTitle}>Serving Size Methodology</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
              <tbody>
                {[
                  ['Declared serving size',     `${fmt(profile?.servingSize, 0)} g${profile?.servingSizeLabel ? ` (${profile.servingSizeLabel})` : ''}`],
                  ['Servings per container',    fmt(profile?.servingsPerContainer, 1)],
                  ['Total finished weight',     `${fmt(profile?.productWeightG, 0)} g per unit`],
                  ['Moisture loss (baking)',    `${fmt(profile?.weightLossPct, 1)}%`],
                  ['Concentration factor',      `${concentrationFactor}× (1 / (1 − weightLoss%))`],
                  ['Serving size authority',    '21 CFR 101.12 Table 2 — RACC'],
                  ['Calculation standard',      '21 CFR 101.9(g) — weighted average per ingredient'],
                ].map(([k, v], i) => (
                  <tr key={i}>
                    <td style={{ ...td, background: i % 2 === 0 ? '#f8f9fa' : '#fff', fontWeight: 500, width: '55%' }}>{k}</td>
                    <td style={{ ...td, background: i % 2 === 0 ? '#f8f9fa' : '#fff' }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Calculation narrative */}
            <div style={sectionTitle}>Calculation Methodology</div>
            <div style={{ fontSize: '10px', lineHeight: '1.6', color: '#333' }}>
              <p style={{ margin: '0 0 6px' }}>
                Nutrient values were calculated from the product recipe using the following method:
              </p>
              <ol style={{ margin: '0', paddingLeft: '16px' }}>
                <li>Each ingredient's per-100g nutrient values were obtained from the source indicated in the Data Sources table below.</li>
                <li>For each ingredient: <code>nutrient_amount = (per_100g_value / 100) × ingredient_weight_g</code></li>
                <li>All ingredient amounts were summed to obtain total raw batch nutrients.</li>
                {profile?.weightLossPct > 0 && (
                  <li>A concentration factor of <strong>{concentrationFactor}×</strong> was applied to account for {fmt(profile.weightLossPct, 1)}% moisture loss during baking per product specification.</li>
                )}
                <li>Nutrients were normalized to the declared serving size: <code>per_serving = total × (serving_g / product_weight_g)</code></li>
                <li>Each nutrient was rounded per its specific rule under 21 CFR 101.9(c) (see Rounding Audit table).</li>
                <li>% Daily Values calculated using 2,000 kcal/day reference amounts per 21 CFR 101.9(c)(8)(iv).</li>
              </ol>
              {coveragePct < 100 && (
                <p style={{ margin: '6px 0 0', color: '#856404', background: '#fff3cd', padding: '4px 8px', borderRadius: '3px' }}>
                  ⚠ Note: {100 - coveragePct}% of recipe ingredients are missing nutrition data.
                  Declared values represent a partial estimate. Complete all ingredient data for a fully verified label.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Recipe Ingredients (with sub-ingredients) ── */}
      <div style={sectionTitle}>Recipe Ingredients &amp; Sub-Ingredients</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
        <thead>
          <tr>
            <th style={th}>Ingredient</th>
            <th style={th}>Manufacturer</th>
            <th style={{ ...th, width: '35%' }}>Sub-Ingredients (from spec sheet)</th>
            <th style={th}>Allergens</th>
            <th style={th}>Qty (kg)</th>
          </tr>
        </thead>
        <tbody>
          {ingredientSources.map((ing, i) => {
            const subs = ing.subIngredients || [];
            const allergens = ing.allergenFlags || [];
            return (
              <tr key={`${ing.ingredientId}-${i}`}>
                <td style={i % 2 === 0 ? td : tdAlt}>
                  <strong>{ing.name}</strong>
                  {ing.baseRecipeName && (
                    <span style={{ marginLeft: '5px', fontSize: '9px', fontWeight: 400,
                      background: '#e2e8f0', color: '#475569', borderRadius: '3px', padding: '1px 4px' }}>
                      via {ing.baseRecipeName}
                    </span>
                  )}
                </td>
                <td style={i % 2 === 0 ? td : tdAlt}>{ing.manufacturer || '—'}</td>
                <td style={i % 2 === 0 ? td : tdAlt}>
                  {subs.length > 0
                    ? subs.map(s => s.name).join(', ')
                    : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td style={i % 2 === 0 ? td : tdAlt}>
                  {allergens.length > 0
                    ? allergens.map(a => (
                        <span key={a} style={{ background: '#fff3cd', color: '#856404',
                          borderRadius: '3px', padding: '1px 4px', marginRight: '3px',
                          fontSize: '9px', fontWeight: 600, textTransform: 'uppercase' }}>
                          {a}
                        </span>
                      ))
                    : <span style={{ color: '#aaa' }}>None declared</span>}
                </td>
                <td style={i % 2 === 0 ? td : tdAlt}>{fmt(ing.expectedQtyKg, 3)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Ingredient Nutrition Data Sources ── */}
      <div style={sectionTitle}>Ingredient Nutrition Data Sources</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Ingredient</th>
            <th style={th}>Data Source</th>
            <th style={th}>Reference</th>
            <th style={th}>Data Date</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {ingredientSources.map((ing, i) => (
            <tr key={`src-${ing.ingredientId}-${i}`}>
              <td style={i % 2 === 0 ? td : tdAlt}>
                <strong>{ing.name}</strong>
                {ing.baseRecipeName && (
                  <span style={{ marginLeft: '5px', fontSize: '9px', fontWeight: 400,
                    background: '#e2e8f0', color: '#475569', borderRadius: '3px', padding: '1px 4px' }}>
                    via {ing.baseRecipeName}
                  </span>
                )}
              </td>
              <td style={i % 2 === 0 ? td : tdAlt}>
                {ing.dataSource === 'fdc'          && <span style={{ color: '#0d6efd', fontWeight: 600 }}>USDA FoodData Central</span>}
                {ing.dataSource === 'fdc_overridden'&& <span style={{ color: '#6f42c1', fontWeight: 600 }}>USDA FDC (modified)</span>}
                {ing.dataSource === 'manual'        && <span style={{ color: '#fd7e14', fontWeight: 600 }}>Manual Entry</span>}
                {!ing.dataSource                    && <span style={{ color: '#dc3545' }}>No data</span>}
              </td>
              <td style={i % 2 === 0 ? td : tdAlt}>
                {ing.fdcId
                  ? <span>FDC #{ing.fdcId}{ing.fdcDescription ? ` — ${ing.fdcDescription}` : ''}</span>
                  : ing.dataSource === 'manual' || ing.dataSource === 'fdc_overridden'
                    ? (
                      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '1px' }}>
                        <span>
                          {ing.specSheetDocumentName
                            ? <strong>{ing.specSheetDocumentName}</strong>
                            : 'Spec sheet / proprietary data'}
                          {ing.specSheetDocumentId && (
                            <span style={{ color: '#0d6efd', marginLeft: '4px' }}>[linked]</span>
                          )}
                        </span>
                        {ing.specSheetDate && (
                          <span style={{ color: '#6c757d', fontSize: '0.85em' }}>{fmtDate(ing.specSheetDate)}</span>
                        )}
                      </span>
                    )
                    : '—'}
              </td>
              <td style={i % 2 === 0 ? td : tdAlt}>{fmtDate(ing.lastSyncedAt)}</td>
              <td style={{ ...(i % 2 === 0 ? td : tdAlt),
                color: ing.hasData ? '#198754' : '#dc3545', fontWeight: 600 }}>
                {ing.hasData ? '✓ Complete' : '✗ Missing'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Rounding Audit ── */}
      <div style={sectionTitle}>Nutrient Rounding Audit (21 CFR 101.9(c))</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Nutrient</th>
            <th style={th}>Calculated (pre-round)</th>
            <th style={th}>Declared (rounded)</th>
            <th style={th}>DV %</th>
            <th style={{ ...th, minWidth: 200 }}>Rounding Rule</th>
            <th style={th}>CFR Reference</th>
          </tr>
        </thead>
        <tbody>
          {ROUNDING_RULES.map((r, i) => {
            const key = NUTRIENT_KEYS.find(n => n.label === r.nutrient)?.key;
            const declared = profile ? profile[key] : null;
            let dv = null;
            if (key && DAILY_VALUES[key] && declared != null) {
              const raw = (Number(declared) / DAILY_VALUES[key]) * 100;
              dv = MICRO_DV_KEYS.includes(key) ? fdaRoundMicroDV(raw) : Math.round(raw);
            }
            return (
              <tr key={r.nutrient}>
                <td style={i % 2 === 0 ? td : tdAlt}><strong>{r.nutrient}</strong></td>
                <td style={i % 2 === 0 ? td : tdAlt}>
                  <span style={{ color: '#888', fontSize: '10px' }}>see recipe calc</span>
                </td>
                <td style={{ ...(i % 2 === 0 ? td : tdAlt), fontWeight: 600 }}>
                  {declared != null ? `${Number(declared).toFixed(1)} ${r.unit}` : '—'}
                </td>
                <td style={i % 2 === 0 ? td : tdAlt}>{dv != null ? `${dv}%` : '—'}</td>
                <td style={i % 2 === 0 ? td : tdAlt}>{r.rule}</td>
                <td style={{ ...(i % 2 === 0 ? td : tdAlt), color: '#0d6efd' }}>{r.cfr}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Approval Audit Trail ── */}
      {version && (
        <>
          <div style={sectionTitle}>Label Approval Audit Trail</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
            <tbody>
              {[
                ['Version number',  `v${version.versionNumber}`],
                ['Status',          'Approved & Locked'],
                ['Approved',        [fmtDate(version.approvedAt), version.approvedBy].filter(Boolean).join(' — ')],
                ['Created',         [fmtDate(version.createdAt),  version.createdBy].filter(Boolean).join(' — ')],
                ['Notes',           version.notes || '—'],
              ].map(([k, v], i) => (
                <tr key={k}>
                  <td style={{ ...td, background: i % 2 === 0 ? '#f8f9fa' : '#fff', fontWeight: 500, width: '30%' }}>{k}</td>
                  <td style={{ ...td, background: i % 2 === 0 ? '#f8f9fa' : '#fff' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ── Footer ── */}
      <div style={{ marginTop: '24px', borderTop: '1px solid #ccc', paddingTop: '10px',
        fontSize: '9px', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: '2px' }}>Generated by QuickLOG Regulatory Module · v2</div>
          Values are calculated from recipe data and ingredient nutrition information provided by the operator.
          This document is intended to support regulatory compliance but does not constitute legal advice.
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap', marginLeft: '16px' }}>
          Generated: {fmtDate(generatedAt)}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// NutritionCertificateButton — drop-in button that loads + prints the certificate
// ─────────────────────────────────────────────────────────────────────────────
const NutritionCertificateButton = ({ productId, className = 'btn btn-outline-secondary btn-sm' }) => {
  const [loading, setLoading] = useState(false);
  const [certData, setCertData] = useState(null);
  const [error, setError] = useState('');
  const [printReady, setPrintReady] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getNutritionCertificate(productId);
      setCertData(data);
      setPrintReady(true);
      setTimeout(() => {
        window.print();
        setPrintReady(false);
      }, 400);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  };

  return (
    <>
      <button className={className} onClick={handleExport} disabled={loading}>
        {loading ? 'Loading…' : 'Export Compliance Certificate'}
      </button>
      {error && <span className="text-danger ms-2 small">{error}</span>}

      {printReady && certData && ReactDOM.createPortal(
        <>
          <style>{`
            @media print {
              #root { display: none !important; }
              #nutrition-cert-portal { display: block !important; }
              @page { margin: 0.4in; size: letter portrait; }
            }
          `}</style>
          <div id="nutrition-cert-portal" style={{ display: 'none' }}>
            <CertificateDoc data={certData} />
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export { CertificateDoc, NutritionCertificateButton };
export default NutritionCertificateButton;
